<template>
  <v-hover v-slot:default="{ hover }">
    <v-card
      tile
      :color="getBackgroundColor(hover)"
      style="border-radius: 0px !important"
      class="my-1 pa-0"
      :elevation="hover ? 8 : 1"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
    >
      <v-card-text class="pa-1">
        <DocumentItemToolbar
          :v-if="!document.isDeleted"
          :documentId="document.id"
          :permissions="document.permissions"
          :commentsCount="document.totalComments"
          :attachmentsCount="document.totalAttachments"
          :alertsCount="document.totalAlerts"
          :canUnblock="document.canUnblock"
          :isBlocked="document.isBlocked"
          :lockInfo="document.lockInfo"
          :isDistributed="document.isDistributed"
          :isInWorkflow="document.isInWorkflow"
          :isPendingConfirmation="document.isPendingConfirmation"
          :isLastVersion="document.isLastVersion"
          :hasPlugins="document.hasPlugins"
          :isPdf="document.extension.toLowerCase() == '.pdf'"
          @on-document-removed="onDocumentRemoved"
          @on-version-removed="onVersionRemoved"
          @on-document-blocked="onDocumentBlocked"
          @on-reception-confirmed="onReceptionConfirmed"
          @on-selection-change="onSelectionChange"
          @on-action-click="onActionClick"
          @on-edit-click="onEditClick"
          @on-flow-diagram-click="onFlowDiagramClick"
          @on-sign-click="onSignClick"
          @on-load-plugin-click="onloadPluginClick"
          :selectedDocuments="selectedDocuments"
        />

        <CommentList
          v-if="showComments"
          :permissions="document.permissions"
          :documentId="document.id"
          @on-close-click="onActionClick('comment')"
          @on-comments-updated="onCommentsUpdated"
        />

        <AlertList
          v-if="showAlerts"
          :documentId="document.id"
          @on-close-click="onActionClick('alert')"
          @on-alert-updated="onAlertUpdated"
        />

        <VersionList
          v-if="showVersions"
          :documentId="document.id"
          @on-close-click="onActionClick('version')"
          @on-version-click="onVersionClick"
        />

        <HistoryList
          v-if="showHistory"
          :documentId="document.id"
          @on-close-click="onActionClick('history')"
        />

        <AttachmentList
          v-if="showAttachments"
          :permissions="document.permissions"
          :documentId="document.id"
          @on-close-click="onActionClick('attachment')"
          @on-attachment-updated="onAttachmentsUpdated"
        />

        <DistributionList
          v-if="showDistributions"
          :documentId="document.id"
          @on-close-click="onActionClick('distribution')"
          @on-distribution-ended="onDistributionEnded"
        />

        <SignatureList
          v-if="showSignatures"
          :documentId="document.id"
          @on-close-click="onActionClick('signatures')"
        />

        <DocumentItemFields
          :fieldList="getFields"
          :documentId="document.id"
          @on-fields-click="onFieldsClick"
        />

        <!-- <template v-if="fieldExpanded">
          <div v-for="group in document.groups" v-bind:key="group.name">
            <span class="font-weight-bold ma-1 text-uppercase">{{group.name}}</span>
            <document-item-fields :fieldList="group.fields" :documentId="document.id" />
          </div>
        </template> -->
        <DocumentItemFooter
          :documentVersion="document.version"
          :contentType="document.contentType"
          :fileExtension="document.extension"
          :isLastVersion="document.isLastVersion"
          :isDigitallySigned="document.isDigitallySigned"
          :isPdf="document.extension.toLowerCase() == '.pdf'"
          @on-action-click="onActionClick"
        />
      </v-card-text>
    </v-card>
  </v-hover>
</template>

<script>
import DocumentItemFooter from "@/components/search/results/list/item/DocumentItemFooter";
import DocumentItemToolbar from "@/components/search/results/list/item/DocumentItemToolbar";
import DocumentItemFields from "@/components/search/results/list/item/DocumentItemFields";
import CommentList from "@/components/search/results/list/item/actions/comment/CommentList";
import VersionList from "@/components/search/results/list/item/actions/version/VersionList";
import HistoryList from "@/components/search/results/list/item/actions/history/HistoryList";
import AlertList from "@/components/search/results/list/item/actions/alert/AlertList";
import AttachmentList from "@/components/search/results/list/item/actions/attachment/AttachmentList";
import DistributionList from "@/components/search/results/list/item/actions/distribution/DistributionList";
import SignatureList from "@/components/search/results/list/item/actions/signature/SignatureList";
import { EventBus } from "@/eventBus.js";
import SearchResultItem from "@/ecm/models/search/results/SearchResultItem";

export default {
  components: {
    DocumentItemFooter,
    DocumentItemToolbar,
    DocumentItemFields,
    CommentList,
    VersionList,
    HistoryList,
    AlertList,
    AttachmentList,
    DistributionList,
    SignatureList,
  },
  props: {
    documentDto: {
      type: Object,
    },
    isSelected: {
      type: Boolean,
    },
    fieldsCount: {
      type: Number,
      default: 4,
    },
    selectedDocuments: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showAttachments: false,
      showVersions: false,
      showComments: false,
      showHistory: false,
      showAlerts: false,
      showDistributions: false,
      showSignatures: false,
      fieldExpanded: false,
    };
  },
  computed: {
    getFields() {
      if (this.fieldExpanded) {
        return this.document.fields;
      }
      return this.document.fields.filter(f => f.isVisible).slice(0, this.fieldsCount);
    },
    document() {
      return new SearchResultItem(this.documentDto);
    },
  },
  methods: {
    getBackgroundColor(hover) {
      if (this.isSelected) return "blue lighten-5";
      if (hover) return "grey lighten-2";
      if (this.document.isBlocked) return "amber lighten-3";
      return "grey lighten-5";
    },
    onFieldsClick() {
      this.$emit("on-content-click", this.document.id);
    },
    onEditClick() {
      this.$emit("on-edit-click", this.document.id);
    },
    onFlowDiagramClick() {
      this.$emit("on-flow-diagram-click", this.document.id);
    },
    onSignClick() {
      this.$emit("on-sign-click", this.document.id);
    },
    onActionClick(action) {
      var fieldExpanded = false;
      var showComments = false;
      var showVersions = false;
      var showAttachments = false;
      var showHistory = false;
      var showAlerts = false;
      var showDistributions = false;
      var showSignatures = false;

      switch (action) {
        case "comment":
          showComments = !this.showComments;
          break;
        case "expand":
          fieldExpanded = !this.fieldExpanded;
          break;
        case "version":
          showVersions = !this.showVersions;
          break;
        case "history":
          showHistory = !this.showHistory;
          break;
        case "alert":
          showAlerts = !this.showAlerts;
          break;
        case "attachment":
          showAttachments = !this.showAttachments;
          break;
        case "distribution":
          showDistributions = !this.showDistributions;
          break;
        case "signatures":
          showSignatures = !this.showSignatures;
          break;
      }

      this.fieldExpanded = fieldExpanded;
      this.showComments = showComments;
      this.showVersions = showVersions;
      this.showAttachments = showAttachments;
      this.showHistory = showHistory;
      this.showAlerts = showAlerts;
      this.showDistributions = showDistributions;
      this.showSignatures = showSignatures;
    },
    onCommentsUpdated(count) {
      if (count != this.document.totalComments)
        this.document.totalComments = count;
    },
    onAttachmentsUpdated(count) {
      if (count != this.document.totalAttachments)
        this.document.totalAttachments = count;
    },
    onAlertUpdated(count) {
      if (count != this.document.totalAlerts) this.document.totalAlerts = count;
    },
    onVersionClick(newObjectID) {
      const oldObjectID = this.document.id;
      this.$emit("on-version-change", { oldObjectID, newObjectID });
    },
    onSelectionChange(isSelected) {
      const documentId = this.document.id;
      this.$emit("on-selection-change", { isSelected, documentId });
    },
    onDocumentRemoved() {
      this.$emit("on-document-removed", this.document.id);
    },
    onDistributionEnded() {
      this.showDistributions = false;
      this.$emit("on-distribution-ended", this.document.id);
    },
    onVersionRemoved(newObjectID) {
      const oldObjectID = this.document.id;
      this.$emit("on-version-removed", { oldObjectID, newObjectID });
    },
    onDocumentBlocked() {
      this.$emit("on-document-blocked", this.document.id);
    },
    onReceptionConfirmed() {
      this.$emit("on-reception-confirmed", this.document.id);
    },
    onloadPluginClick(plugin) {
      const objectIDs = [this.document.id];
      this.$emit("on-load-plugin-click", { plugin, objectIDs });
    },
    dragover(event) {
      event.preventDefault();
      if (!event.currentTarget.classList.contains("yellow")) {
        event.currentTarget.classList.remove("grey");
        event.currentTarget.classList.remove("lighten-5");
        event.currentTarget.classList.add("yellow");
        event.currentTarget.classList.add("lighten-3");
      }
    },
    dragleave(event) {
      event.currentTarget.classList.add("grey");
      event.currentTarget.classList.add("lighten-5");
      event.currentTarget.classList.remove("yellow");
      event.currentTarget.classList.remove("lighten-3");
    },
    drop(event) {
      event.preventDefault();
      var files = event.dataTransfer.files;
      if (files.length > 0) {
        EventBus.$emit("dragAndDrop", {
          documentId: this.document.id,
          files: files,
        });
      }
      event.currentTarget.classList.add("grey");
      event.currentTarget.classList.add("lighten-5");
      event.currentTarget.classList.remove("yellow");
      event.currentTarget.classList.remove("lighten-3");
    },
  },
};
</script>
